import '@/assets/globalStyles.scss';

import { captureException, setTag, withScope } from '@sentry/browser';
import { type Client, install as urqlPlugin, type SSRData, type SSRExchange } from '@urql/vue';
// @ts-expect-error does not offer types
import vClickOutside from 'click-outside-vue3';
import type { Pinia } from 'pinia';
import {
  type App,
  type Component,
  createApp as createVueApp,
  createSSRApp,
  defineAsyncComponent,
  h,
} from 'vue';
import VueCookies from 'vue-cookies';

import type { MarkSsrError } from '@/../server/server';
import { ASSORTMENT_SUPPLIER } from '@/composables/salesChannelFilter';
import { getCookieFunction } from '@/composables/ssrCookies';
import type { MarkSsrOnly } from '@/composables/ssrOnly';
import createDictPlugin, { type DictionaryPhrases } from '@/lib/dictPlugin';
import fischerPlugin, { createFischerStore, type FischerPluginStore } from '@/lib/fischerPlugin';
import { useBestPriceStore } from '@/store/pinia/bestPrice';
import { useCartStore } from '@/store/pinia/cart';
import { useLayoutStore } from '@/store/pinia/layout';
import { useSearchStore } from '@/store/pinia/search';
import VueLazyload from '@/vue-lazyload/src';

import { createGraphQLClient, createSsrExchange, type FischerBox } from './createGraphQLClient';
import intersect from './directives/intersect';
import SitecoreJssStorePlugin from './lib/SitecoreJssStorePlugin.js';
import loadLayoutData from './loadLayoutData';
import { normalizeSellingUnitKeys } from './normalizeSellingUnitKeys';
import setupDictionary from './setupDictionary';
import type {
  InitialState,
  SitecoreDeploymentEnv,
  SitecoreRendering,
  SitecoreState,
} from './sitecoreState';
import {
  createSsrOperationRecorder,
  type SsrOperationRecorder,
  type SsrOperations,
} from './ssrOperationRecorder';
import {
  createSsrRestoreReporter,
  describeFinding,
  type SsrRestoreReporter,
} from './ssrRestoreReporter';

const pageModules = import.meta.glob<{ default: Component }>('@/pages/**/*.vue');

const pageComponentsByName = new Map<string, () => Promise<{ default: Component }>>();
for (const [path, importer] of Object.entries(pageModules)) {
  const name = path.match(/\/([^/]+)\.vue$/)?.[1];
  if (name) {
    pageComponentsByName.set(name, importer);
  }
}

// ExperienceEditorComponent lives outside src/pages/
pageComponentsByName.set(
  'ExperienceEditorComponent',
  () => import('@/components/atoms/experience-editor-component/ExperienceEditorComponent.vue'),
);

async function resolveComponent(name: string): Promise<Component | null> {
  const importer = pageComponentsByName.get(name);
  if (!importer) {
    console.error('component not found', name);
    return null;
  }
  try {
    const mod = await importer();
    return mod?.default ?? null;
  } catch (error) {
    console.error('Error importing component', error);
    return null;
  }
}

const importShowroomOverrides = () =>
  import('@/components/dev/showroom-settings/localStorageSettings');

const addRouterToApp = async (app: App) => {
  const { createRouter } = await import('./showroom-backend/router.js');
  const router = createRouter();
  app.use(router);
};

if (
  IS_SHOWROOM &&
  !import.meta.env.VITE_NO_SHOWROOM_COOKIEBOT &&
  !document.getElementById('Cookiebot')
) {
  const script = document.createElement('script');
  script.id = 'Cookiebot';
  script.src = 'https://consent.cookiebot.com/uc.js';
  script.setAttribute('type', 'text/javascript');
  script.dataset.cbid = 'f77b284f-3975-46d3-81b3-df53f1839764';
  script.dataset.blockingmode = 'auto';
  document.head.appendChild(script);
}

// creates breakpoint helper element and appends it to the body
function createBreakpointHelper() {
  if (!IS_SSR) {
    const hasBeakpointHelperElement = document.getElementById('breakpoint-helper');
    if (!hasBeakpointHelperElement) {
      const breakpointHelperElement = document.createElement('div');
      breakpointHelperElement.setAttribute('id', 'breakpoint-helper');
      document.body.appendChild(breakpointHelperElement);
    }
  }
}

// Must stay single-root: Sitecore only skips the cache when the rendering starts
// with the error marker, and a fragment would prefix it with Vue's anchor.
const getComponentNotFoundMessage = (componentName: string, markSsrError?: MarkSsrError) => {
  const msg = `component not found to create: ${componentName}`;
  console.error(msg);
  markSsrError?.(msg);
  return { render: () => h('div', msg) };
};

interface AppState {
  rehydrate?: boolean;
  sitecore?: SitecoreState;
  pinia: Pinia;
  prefetch?: any;
  markSsrOnly?: MarkSsrOnly;
  inhibitMarkSsrOnly?: MarkSsrOnly;
  reloadEditorOnAdd?: () => void;
  client?: Client;
  fischerPluginStore: FischerPluginStore;
  fischerBox: FischerBox;
  dictData: DictionaryPhrases;
}
// createApp is invoked by both the main and SSR entry points, so the two entry points can use the same app creation process.
export async function createApp(appState: AppState): Promise<null | App<Element>> {
  if (!IS_SSR) {
    const fieldKey = appState?.sitecore?.dataSource?.['conditions-field-key']?.value;
    if (fieldKey) {
      const formElement = document.querySelector(
        `.jss-forms-element[conditions-field-key="${fieldKey}"]`,
      );

      // Class 'jss-init-disabled' is added on start dragging elements (see 'FormDesignBoard.js' in fischer-website sitecore project)
      if (formElement?.classList?.contains('jss-init-disabled')) {
        return null;
      }
    }
  }

  const {
    sitecore,
    client,
    markSsrOnly,
    inhibitMarkSsrOnly,
    reloadEditorOnAdd,
    pinia,
    prefetch,
    rehydrate,
    fischerPluginStore,
    fischerBox,
    dictData,
  } = appState;
  const {
    rendering = { enableXPE: false, componentName: '' },
    dataSource,
    customData,
    data: { isEditMode = false } = {},
    placeholders = {},
  } = sitecore || {};
  const componentName =
    isEditMode && !rendering.enableXPE ? 'ExperienceEditorComponent' : rendering.componentName;

  const component: any = componentName ? await resolveComponent(componentName) : null;
  const fields = dataSource || {};
  interface ReducedRendering {
    enableXPE: boolean;
    componentName: string;
  }
  const props: {
    fields?: any;
    customData?: any;
    markSsrOnly?: MarkSsrOnly;
    reloadEditorOnAdd?: AppState['reloadEditorOnAdd'];
    rendering?: SitecoreRendering | ReducedRendering;
    placeholders?: any;
  } = {};
  if (component?.props) {
    if (component.props.fields) {
      props.fields = fields;
    }
    if (component.props.customData) {
      props.customData = customData;
    }
    if (component.props.markSsrOnly) {
      props.markSsrOnly = markSsrOnly;
    }
    if (component.props.reloadEditorOnAdd) {
      props.reloadEditorOnAdd = reloadEditorOnAdd;
    }
    if (component.props.rendering) {
      props.rendering = rendering;
    }
    if (component.props.placeholders) {
      props.placeholders = placeholders;
    }
  }
  const vueOptions = IS_SHOWROOM
    ? (await import('./showroom-backend/AppRoot.vue')).default
    : component ||
      getComponentNotFoundMessage(componentName, fischerPluginStore?.dev?.markSsrError);

  const ssrApp = IS_SSR || rehydrate;
  const app = ssrApp ? createSSRApp(vueOptions, props) : createVueApp(vueOptions, props);

  // `useId` counts from zero per app, and a page is many apps - without a prefix two islands hand
  // out the same id and the `for`/`id` pairs cross over. Keyed by the rendering, so the server and
  // the client derive the same prefix for the same island.
  app.config.idPrefix = componentName || 'v';

  const dictPlugin = createDictPlugin();
  dictPlugin.setData(dictData);

  app.use(vClickOutside);
  app.use(SitecoreJssStorePlugin);
  app.use(fischerPlugin, fischerPluginStore);
  // All apps sharing this box use the same fischerPluginStore, so overwriting is safe.
  fischerBox.plugin = app.config.globalProperties.$fischer;
  app.use(VueCookies);
  app.use(dictPlugin);
  if (client) {
    // @urql/vue's default export is the install function; provides the Client to useQuery et al.
    app.use(urqlPlugin, client);
    app.config.globalProperties.$urqlClient = client;
  }
  app.use(VueLazyload, { lazyComponent: true });
  app.use(pinia);
  app.provide('fischerPlugin', app.config.globalProperties.$fischer);
  app.provide('dictPlugin', app.config.globalProperties.$dict);
  app.provide('sitecoreState', sitecore);
  app.provide('inhibitMarkSsrOnly', props.markSsrOnly && inhibitMarkSsrOnly);

  if (IS_SHOWROOM || (APP_ENVIRONMENT === 'VM' && !IS_SSR)) {
    // @ts-ignore
    window.showroom = {
      $fischer: app.config.globalProperties.$fischer,
      $cookies: app.config.globalProperties.$cookies,
      $dict: app.config.globalProperties.$dict,
    };

    await addRouterToApp(app);
  }
  if (!IS_SHOWROOM) {
    app.provide('fields', fields);
  }
  app.provide('rendering', rendering);
  app.provide('componentFactory', (name: string) => {
    const importer = pageComponentsByName.get(name);
    if (!importer) {
      return null;
    }
    return defineAsyncComponent(importer);
  });
  app.provide('prefetch', prefetch || null);

  app.directive('intersect', intersect);

  app.config.globalProperties.isAppView =
    getCookieFunction(
      'app-view',
      app.config.globalProperties.$fischer,
      app.config.globalProperties.$cookies,
      (rendering as SitecoreRendering) ?? null,
    ) === 'true';

  app.config.globalProperties.isFite = app.config.globalProperties.$fischer.brandId() === 'FITE';
  app.config.globalProperties.isUpat = app.config.globalProperties.$fischer.brandId() === 'UPAT';
  // if there is an initial state defined, push it into the store, where it can be referenced by interested components.
  app.config.globalProperties.IS_SSR = IS_SSR;

  app.config.globalProperties.IS_SHOWROOM = IS_SHOWROOM;
  app.config.globalProperties.isEditMode = isEditMode;

  const activateSalesChannelAssortment =
    app.config.globalProperties.$fischer.shop.activateSalesChannelAssortment();
  const authenticated = app.config.globalProperties.$fischer.user.authenticated();
  const distributionChannel = app.config.globalProperties.$fischer.user.distributionChannel();
  // Keep in sync with useSalesChannelFilter().supplier()
  const suggestionSupplier = () => {
    if (!activateSalesChannelAssortment) {
      return undefined;
    }
    return authenticated ? ASSORTMENT_SUPPLIER : '';
  };

  useSearchStore(pinia).setQueryData({
    apollo: client,
    brandId: app.config.globalProperties.$fischer.brandId(),
    distributionChannels:
      activateSalesChannelAssortment && authenticated
        ? app.config.globalProperties.$fischer.shop.distributionChannels()
        : undefined,
    supplier: suggestionSupplier(),
    publicAssortment: activateSalesChannelAssortment && (!authenticated || !distributionChannel),
    factFinderChannel: app.config.globalProperties.$fischer.factFinderChannel(),
    locale: app.config.globalProperties.$fischer.catalogLocale(),
    hasProducts: Boolean(app.config.globalProperties.$fischer.pages.products().url),
    productUrlBase: app.config.globalProperties.$fischer.pages.products().url,
  });

  if (!IS_SSR) {
    useCartStore(pinia).initialize({
      apollo: client,
      fischer: app.config.globalProperties.$fischer,
    });
    useBestPriceStore(pinia).initialize({
      apollo: client,
      fischer: app.config.globalProperties.$fischer,
    });
  }

  // having to do this is pretty ugly, but fixing it would require forking the whole maps thing
  app.config.globalProperties.$gmapOptions = {
    key: app.config.globalProperties.$fischer.gmapApiKey(),
    libraries: 'places,marker',
    mapId: app.config.globalProperties.$fischer.gmapId(),
  };

  if (IS_SHOWROOM) {
    const { applyStoredSettings } = await importShowroomOverrides();
    applyStoredSettings(app.config.globalProperties.$fischer);
  }

  if (!IS_SSR) {
    const layoutStore = useLayoutStore(pinia);
    const resizeHandler = () =>
      layoutStore.setWindowDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
      });
    window.addEventListener('resize', resizeHandler, true);
    resizeHandler();

    const scrollHandler = () =>
      layoutStore.setWindowScrollPos({
        x: window.scrollX,
        y: window.scrollY,
      });
    window.addEventListener('scroll', scrollHandler, true);
    scrollHandler();
  }

  return app;
}

export async function setupAppPage({
  initialState,
  ssrState,
  ssrOperations: recordedSsrOperations,
  noLayoutData,
  markSsrError,
  pinia,
}: {
  initialState?: InitialState;
  ssrState?: SSRData;
  /** The page's __SSR_OPERATIONS__ blocks, merged. Client-side and debug builds only. */
  ssrOperations?: SsrOperations;
  noLayoutData?: boolean;
  markSsrError?: MarkSsrError;
  pinia: Pinia;
}): Promise<{
  client: Client;
  ssr: SSRExchange;
  /** Debug-only; undefined outside a server render and outside TEST/VM/showroom builds. */
  ssrOperations?: SsrOperationRecorder;
  /** Debug-only; the client's counterpart - says why a server payload was not reused. */
  ssrRestore?: SsrRestoreReporter;
  layoutSucceeded: boolean;
  fischerPluginStore: FischerPluginStore;
  fischerBox: FischerBox;
  dictData: DictionaryPhrases;
}> {
  const sitecoreData = initialState?.sitecore?.data || {};
  const {
    pageId,
    language: languageSitecore,
    host,
    uri,
    isSecureConnection,
    sitecoreDeploymentEnv,
  }: {
    pageId?: string;
    language?: string;
    host?: string;
    uri?: string;
    isSecureConnection?: boolean;
    sitecoreDeploymentEnv?: SitecoreDeploymentEnv;
  } = sitecoreData;

  const language = IS_SHOWROOM
    ? JSON.parse(window.localStorage.getItem('showroomSettings') || '{}')?.translations?.[0] ||
      'de-DE'
    : languageSitecore;

  const baseUrl = `http${isSecureConnection ? 's' : ''}://${host}`;

  setTag('APP environment', APP_ENVIRONMENT);
  setTag('APP host', host);
  setTag('APP uri', uri);
  setTag('APP language', language);
  setTag('APP ssrMode', IS_SSR);

  const fischerBox: FischerBox = { plugin: null };

  const ssr = createSsrExchange();
  // Debug aid, server-side and non-production only: urql's SSR payload keeps no record of which
  // variables produced each entry. Tree-shaken out of every other build.
  const ssrOperations = DEBUG_SSR_OPERATIONS && IS_SSR ? createSsrOperationRecorder() : undefined;
  // The same tap on the other side: what the client asks for, against what the server ran.
  const ssrRestore =
    DEBUG_SSR_OPERATIONS && !IS_SSR && recordedSsrOperations
      ? createSsrRestoreReporter({
          ssrOperations: recordedSsrOperations,
          serialisedKeys: Object.keys(ssrState ?? {}),
          report: (finding) => {
            // eslint-disable-next-line no-console
            console.warn(describeFinding(finding));
          },
        })
      : undefined;
  if (!IS_SSR && ssrState) {
    ssr.restoreData(ssrState);
  }
  const client = createGraphQLClient({
    color: sitecoreDeploymentEnv,
    referrer: IS_SSR && `${baseUrl}${uri}`,
    fischerBox,
    ssr,
    operationRecorder: ssrOperations?.exchange ?? ssrRestore?.exchange,
  });

  const fischerPluginStore = createFischerStore();

  createBreakpointHelper();
  const [dictData, layoutSucceeded] = await Promise.all([
    noLayoutData || !language ? {} : setupDictionary(language, client, initialState, baseUrl),
    noLayoutData
      ? true
      : setupLayoutData(
          pinia,
          baseUrl,
          pageId,
          language,
          initialState,
          VueCookies,
          fischerPluginStore,
          markSsrError,
        ),
  ]);

  return {
    client,
    ssr,
    ssrOperations,
    ssrRestore,
    layoutSucceeded,
    fischerPluginStore,
    fischerBox,
    dictData,
  };
}

const setupLayoutData = async (
  pinia: Pinia,
  baseUrl: string,
  pageId: string | undefined,
  lang: string | undefined,
  state: InitialState | undefined,
  $cookies: any,
  fischerPluginStore: FischerPluginStore,
  markSsrError?: MarkSsrError,
) => {
  try {
    if (IS_SSR && !IS_SHOWROOM) {
      normalizeSellingUnitKeys(state);
      fischerPluginStore.setData(state, state, markSsrError);
      useLayoutStore(pinia).setLayoutData(state);
      return true;
    }

    const isEditMode = state?.sitecore?.data?.isEditMode;
    const siteName =
      (isEditMode && new URLSearchParams(window.location.search).get('sc_site')) || undefined;
    const layoutData = await (window.document.location.search.includes('sc_formmode=edit')
      ? loadLayoutData(baseUrl, undefined, undefined, $cookies, siteName)
      : loadLayoutData(baseUrl, pageId, lang, $cookies, siteName));

    fischerPluginStore.setData(layoutData, state || (IS_SHOWROOM ? layoutData : undefined));
    useLayoutStore(pinia).setLayoutData(layoutData);

    return true;
  } catch (error) {
    if (error instanceof Error && String(error.message).toLowerCase() === 'request aborted') {
      console.info('request was aborted, ie. user most likely navigated away');
    } else {
      console.error('Could not fetch layout data');
      withScope((scope) => {
        scope.setFingerprint(['get layoutData']);
        captureException(error);
      });
      console.error(error);
    }
    return false;
  }
};
